import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  BookOpenCheck,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Cpu,
  ExternalLink,
  FileSearch,
  FlaskConical,
  Link2,
  Plus,
  RadioTower,
  Search,
  ShieldCheck,
  Truck,
  Video,
  X,
  XCircle,
} from "lucide-react";
import {
  createDevice,
  createDeviceBinding,
  listDeviceBindings,
  listDeviceCommissioning,
  listDevices,
  listIntegrationProfiles,
  updateDeviceCommissioning,
} from "../api";
import type {
  ComponentBinding,
  ComponentBindingCreateInput,
  Device,
  DeviceCommissioning,
  DeviceCreateInput,
  IntegrationProfile,
  Vehicle,
} from "../types";

const categories = [
  { value: "telematics", label: "Telemática" },
  { value: "tpms", label: "TPMS" },
  { value: "video", label: "Video" },
  { value: "fuel", label: "Combustible" },
  { value: "can", label: "CAN / J1939" },
  { value: "security", label: "Seguridad" },
];

const stageLabel = {
  planned: "PLANEADO",
  bench: "PRUEBA DE BANCO",
  field: "PRUEBA DE CAMPO",
  certified: "CERTIFICADO",
  failed: "RECHAZADO",
};

const stepLabel: Record<string, string> = {
  identity: "Identidad, serie e IMEI",
  power: "Alimentación y protección eléctrica",
  network: "Registro celular y APN",
  protocol: "Protocolo y decodificación",
  telemetry: "Telemetría extremo a extremo",
  component_mapping: "Enlace con componente 3D",
  storage: "Almacenamiento local",
  day_video: "Video diurno",
  night_video: "Video nocturno",
  event_upload: "Carga de evidencia por evento",
  calibration: "Calibración en cabina",
  driver_consent: "Aviso y consentimiento",
  event_test: "Pruebas DSM/ADAS",
  photo_upload: "Carga de fotografía",
  gnss: "Precisión GNSS",
  sleep_profile: "Perfil de bajo consumo",
  movement_event: "Evento de movimiento",
  battery_baseline: "Línea base de batería",
  ble_pairing: "Emparejamiento BLE",
  temperature_reference: "Referencia de temperatura",
  humidity_reference: "Referencia de humedad",
  door_event: "Evento de puerta",
  pairing_12_wheels: "Emparejamiento de 12 ruedas",
  pressure_reference: "Referencia de presión",
  wheel_mapping: "Posición física de rueda",
  live_session: "Sesión de video en vivo",
};

const emptyForm: DeviceCreateInput = {
  vehicle_id: null,
  serial_number: "",
  category: "telematics",
  manufacturer: "",
  model: "",
  protocol: "",
  connection_type: "",
  status: "planned",
  firmware_version: null,
  configuration: {},
};

const emptyBinding: ComponentBindingCreateInput = {
  source_key: "",
  sensor_code: "",
  component_code: "",
  unit: "",
  thresholds: {},
  transform: {},
  active: true,
};

export function DeviceInventoryPanel({
  open,
  onClose,
  vehicles,
  canManage,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  canManage: boolean;
}) {
  const [view, setView] = useState<"inventory" | "certification" | "catalog">("inventory");
  const [devices, setDevices] = useState<Device[]>([]);
  const [profiles, setProfiles] = useState<IntegrationProfile[]>([]);
  const [commissioning, setCommissioning] = useState<DeviceCommissioning[]>([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [form, setForm] = useState<DeviceCreateInput>(emptyForm);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedCommissioningId, setSelectedCommissioningId] = useState<number | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [bindings, setBindings] = useState<ComponentBinding[]>([]);
  const [bindingForm, setBindingForm] = useState<ComponentBindingCreateInput>(emptyBinding);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [commissioningNotes, setCommissioningNotes] = useState("");
  const [commissioningProfileId, setCommissioningProfileId] = useState<number | null>(null);

  function loadAll() {
    Promise.all([listDevices(), listIntegrationProfiles(), listDeviceCommissioning()])
      .then(([deviceRows, profileRows, commissioningRows]) => {
        setDevices(deviceRows);
        setProfiles(profileRows);
        setCommissioning(commissioningRows);
        setSelectedCommissioningId((current) =>
          commissioningRows.some((row) => row.id === current)
            ? current
            : commissioningRows[0]?.id ?? null,
        );
        setSelectedProfileId((current) =>
          profileRows.some((row) => row.id === current) ? current : profileRows[0]?.id ?? null,
        );
      })
      .catch((reason) => setError(reason.message));
  }

  useEffect(() => {
    if (!open) return;
    loadAll();
    const refresh = () => loadAll();
    window.addEventListener("gsf:commissioning-updated", refresh);
    return () => window.removeEventListener("gsf:commissioning-updated", refresh);
  }, [open]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es-MX");
    return devices.filter((device) => {
      const vehicle = vehicles.find((item) => item.id === device.vehicle_id);
      return (
        (category === "all" || device.category === category) &&
        (!normalized ||
          [device.serial_number, device.manufacturer, device.model, device.protocol, vehicle?.unit_code]
            .filter(Boolean)
            .some((value) => value!.toLocaleLowerCase("es-MX").includes(normalized)))
      );
    });
  }, [category, devices, query, vehicles]);

  const selectedCommissioning =
    commissioning.find((row) => row.id === selectedCommissioningId) || commissioning[0];
  const selectedProfile = profiles.find((row) => row.id === selectedProfileId) || profiles[0];
  const stageCounts = useMemo(
    () => ({
      certified: commissioning.filter((row) => row.stage === "certified").length,
      field: commissioning.filter((row) => row.stage === "field").length,
      pending: commissioning.filter((row) => ["planned", "bench"].includes(row.stage)).length,
      failed: commissioning.filter((row) => row.stage === "failed").length,
    }),
    [commissioning],
  );

  function inspectCommissioning(row: DeviceCommissioning) {
    setSelectedCommissioningId(row.id);
    setChecklist(row.checklist_json);
    setCommissioningNotes(row.notes);
    setCommissioningProfileId(row.profile_id);
    setError("");
    setMessage("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const created = await createDevice(form);
      setDevices((current) => [...current, created]);
      setMessage(`${created.serial_number} quedó registrado`);
      setForm(emptyForm);
      loadAll();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No fue posible registrar el equipo");
    } finally {
      setLoading(false);
    }
  }

  function inspectDevice(device: Device) {
    setSelectedDevice(device);
    setError("");
    listDeviceBindings(device.id).then(setBindings).catch((reason) => setError(reason.message));
  }

  async function submitBinding(event: FormEvent) {
    event.preventDefault();
    if (!selectedDevice) return;
    setLoading(true);
    setError("");
    try {
      const created = await createDeviceBinding(selectedDevice.id, bindingForm);
      setBindings((current) => [...current, created]);
      setBindingForm(emptyBinding);
      setMessage(`${created.source_key} quedó enlazado al gemelo digital`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No fue posible crear el binding");
    } finally {
      setLoading(false);
    }
  }

  async function saveCommissioning(stage: DeviceCommissioning["stage"]) {
    if (!selectedCommissioning) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const updated = await updateDeviceCommissioning(selectedCommissioning.device_id, {
        profile_id: commissioningProfileId ?? undefined,
        stage,
        checklist_json: checklist,
        notes: commissioningNotes,
      });
      setCommissioning((current) => current.map((row) => row.id === updated.id ? updated : row));
      inspectCommissioning(updated);
      setMessage(`${updated.device_serial_number}: ${stageLabel[updated.stage]}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No fue posible actualizar");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;
  const installed = devices.filter((device) => device.status === "installed").length;
  const planned = devices.filter((device) => device.status === "planned").length;

  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel device-panel integration-panel" role="dialog" aria-modal="true" aria-labelledby="device-panel-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">CMDB VEHICULAR · LABORATORIO DE INTEGRACIONES</span>
            <h2 id="device-panel-title">Aparatos, perfiles y certificación</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar integraciones"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Cpu size={17} /><b>{devices.length}</b> aparatos</span>
          <span><RadioTower size={17} /><b>{installed}</b> instalados</span>
          <span><Video size={17} /><b>{planned}</b> por cotizar</span>
          <span><BadgeCheck size={17} /><b>{stageCounts.certified}</b> certificados</span>
        </section>
        <nav className="integration-tabs">
          <button type="button" className={view === "inventory" ? "is-active" : ""} onClick={() => setView("inventory")}><Cpu size={13} /> INVENTARIO</button>
          <button type="button" className={view === "certification" ? "is-active" : ""} onClick={() => setView("certification")}><FlaskConical size={13} /> CERTIFICACIÓN</button>
          <button type="button" className={view === "catalog" ? "is-active" : ""} onClick={() => setView("catalog")}><BookOpenCheck size={13} /> CATÁLOGO TÉCNICO</button>
        </nav>

        {view === "inventory" && (
          <div className="device-panel__body">
            <section>
              <div className="device-toolbar">
                <label><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Serie, unidad, marca o protocolo..." /></label>
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  <option value="all">Todas las categorías</option>
                  {categories.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div className="device-table">
                <header><span>UNIDAD / SERIE</span><span>EQUIPO</span><span>ENLACE</span><span>ESTADO</span></header>
                {filtered.map((device) => {
                  const vehicle = vehicles.find((item) => item.id === device.vehicle_id);
                  return (
                    <article className={selectedDevice?.id === device.id ? "is-selected" : ""} role="button" tabIndex={0} onClick={() => inspectDevice(device)} onKeyDown={(event) => { if (event.key === "Enter") inspectDevice(device); }} key={device.id}>
                      <span><strong>{vehicle?.unit_code || "SIN ASIGNAR"}</strong><small>{device.serial_number}</small></span>
                      <span><strong>{device.manufacturer}</strong><small>{device.model}</small></span>
                      <span><strong>{device.connection_type || "—"}</strong><small>{device.protocol || "Sin protocolo"}</small></span>
                      <em className={`device-status device-status--${device.status}`}>{device.status}</em>
                    </article>
                  );
                })}
              </div>
            </section>
            <section className="device-create">
              {selectedDevice ? (
                <>
                  <div className="admin-section-title device-detail-title"><button type="button" onClick={() => setSelectedDevice(null)}><ArrowLeft size={14} /> NUEVO APARATO</button><b>{selectedDevice.serial_number}</b></div>
                  <div className="device-detail"><span className="eyebrow">MAPEO DE SEÑALES</span><h3>{selectedDevice.manufacturer} {selectedDevice.model}</h3><p>{selectedDevice.protocol} · {selectedDevice.connection_type}</p></div>
                  <div className="binding-list">
                    {bindings.map((binding) => (
                      <article key={binding.id}><Link2 size={14} /><span><strong>{binding.source_key}</strong><small>ORIGEN</small></span><span><strong>{binding.sensor_code}</strong><small>SENSOR</small></span><span><strong>{binding.component_code}</strong><small>COMPONENTE 3D</small></span><em>{binding.unit || "—"}</em></article>
                    ))}
                    {!bindings.length && <p>SIN SEÑALES MAPEADAS</p>}
                  </div>
                  <form onSubmit={submitBinding}>
                    <label>CLAVE DE ORIGEN<input required disabled={!canManage} value={bindingForm.source_key} onChange={(event) => setBindingForm({ ...bindingForm, source_key: event.target.value })} placeholder="io.239 o can.spn.110" /></label>
                    <label>SENSOR NORMALIZADO<input required disabled={!canManage} value={bindingForm.sensor_code} onChange={(event) => setBindingForm({ ...bindingForm, sensor_code: event.target.value })} /></label>
                    <label>COMPONENTE 3D<input required disabled={!canManage} value={bindingForm.component_code} onChange={(event) => setBindingForm({ ...bindingForm, component_code: event.target.value })} /></label>
                    <label>UNIDAD<input disabled={!canManage} value={bindingForm.unit} onChange={(event) => setBindingForm({ ...bindingForm, unit: event.target.value })} /></label>
                    {canManage && <button className="primary-button" disabled={loading}><Link2 size={15} /> CREAR BINDING</button>}
                  </form>
                </>
              ) : (
                <>
                  <div className="admin-section-title"><span className="eyebrow">REGISTRAR APARATO</span><b>{canManage ? "Alta auditada" : "Sólo consulta"}</b></div>
                  <form onSubmit={submit}>
                    <label>UNIDAD<select required disabled={!canManage} value={form.vehicle_id ?? ""} onChange={(event) => setForm({ ...form, vehicle_id: Number(event.target.value) })}><option value="">Seleccionar...</option>{vehicles.map((vehicle) => <option value={vehicle.id} key={vehicle.id}>{vehicle.unit_code}</option>)}</select></label>
                    <label>NÚMERO DE SERIE<input required disabled={!canManage} value={form.serial_number} onChange={(event) => setForm({ ...form, serial_number: event.target.value })} /></label>
                    <label>CATEGORÍA<select disabled={!canManage} value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>{categories.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label>
                    <label>ESTADO<select disabled={!canManage} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="planned">Por cotizar</option><option value="staged">En preparación</option><option value="installed">Instalado</option></select></label>
                    <label>FABRICANTE<input required disabled={!canManage} value={form.manufacturer} onChange={(event) => setForm({ ...form, manufacturer: event.target.value })} /></label>
                    <label>MODELO<input required disabled={!canManage} value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} /></label>
                    <label>PROTOCOLO<input disabled={!canManage} value={form.protocol} onChange={(event) => setForm({ ...form, protocol: event.target.value })} /></label>
                    <label>CONEXIÓN<input disabled={!canManage} value={form.connection_type} onChange={(event) => setForm({ ...form, connection_type: event.target.value })} /></label>
                    {canManage && <button className="primary-button" disabled={loading}><Plus size={15} /> REGISTRAR EQUIPO</button>}
                  </form>
                </>
              )}
              {error && <p className="admin-form-message is-error">{error}</p>}
              {message && <p className="admin-form-message is-success">{message}</p>}
            </section>
          </div>
        )}

        {view === "certification" && (
          <div className="commissioning-body">
            <section className="commissioning-queue">
              <div className="commissioning-kpis">
                <span><BadgeCheck size={14} /> {stageCounts.certified} certificados</span>
                <span><Truck size={14} /> {stageCounts.field} en campo</span>
                <span><CircleDashed size={14} /> {stageCounts.pending} pendientes</span>
                <span><XCircle size={14} /> {stageCounts.failed} rechazados</span>
              </div>
              <div className="commissioning-list">
                {commissioning.map((row) => (
                  <button type="button" className={`commissioning-item commissioning-item--${row.stage}${selectedCommissioning?.id === row.id ? " is-selected" : ""}`} onClick={() => inspectCommissioning(row)} key={row.id}>
                    <span>{row.stage === "certified" ? <BadgeCheck size={16} /> : row.stage === "failed" ? <XCircle size={16} /> : <FlaskConical size={16} />}</span>
                    <span><small>{row.vehicle_unit_code || "SIN UNIDAD"} · {row.device_serial_number}</small><strong>{row.profile_name || "Perfil pendiente"}</strong><em>{row.binding_count} señales · {row.missing_steps.length} pruebas pendientes</em></span>
                    <b>{row.readiness_percent}%</b>
                    <i><em style={{ width: `${row.readiness_percent}%` }} /></i>
                  </button>
                ))}
              </div>
            </section>
            <section className="commissioning-detail">
              {selectedCommissioning && (
                <>
                  <div className={`commissioning-stage commissioning-stage--${selectedCommissioning.stage}`}><FlaskConical size={32} /><span><small>ETAPA ACTUAL</small><b>{stageLabel[selectedCommissioning.stage]}</b></span></div>
                  <div className="commissioning-title"><span className="eyebrow">{selectedCommissioning.device_serial_number}</span><h3>{selectedCommissioning.profile_name || "Sin perfil técnico"}</h3><p>{selectedCommissioning.vehicle_unit_code || "Sin unidad"} · {selectedCommissioning.binding_count} bindings activos</p></div>
                  <label className="commissioning-profile">PERFIL DE INTEGRACIÓN
                    <select disabled={!canManage} value={commissioningProfileId ?? selectedCommissioning.profile_id ?? ""} onChange={(event) => setCommissioningProfileId(Number(event.target.value))}>
                      <option value="">Seleccionar perfil...</option>
                      {profiles.filter((profile) => profile.category === selectedCommissioning.category).map((profile) => <option value={profile.id} key={profile.id}>{profile.code} · {profile.manufacturer} {profile.model}</option>)}
                    </select>
                  </label>
                  <div className="commissioning-checklist">
                    <span className="eyebrow">PROTOCOLO DE ACEPTACIÓN</span>
                    {selectedCommissioning.required_steps.map((step) => (
                      <button type="button" disabled={!canManage} className={checklist[step] ?? selectedCommissioning.checklist_json[step] ? "is-complete" : ""} onClick={() => setChecklist((current) => ({ ...selectedCommissioning.checklist_json, ...current, [step]: !(current[step] ?? selectedCommissioning.checklist_json[step]) }))} key={step}>
                        {checklist[step] ?? selectedCommissioning.checklist_json[step] ? <CheckCircle2 size={15} /> : <CircleDashed size={15} />}
                        <span><strong>{stepLabel[step] || step}</strong><small>{step}</small></span>
                      </button>
                    ))}
                  </div>
                  <label className="commissioning-notes">NOTAS DE INGENIERÍA<textarea disabled={!canManage} value={commissioningNotes || selectedCommissioning.notes} onChange={(event) => setCommissioningNotes(event.target.value)} /></label>
                  {error && <p className="admin-form-message is-error">{error}</p>}
                  {message && <p className="admin-form-message is-success">{message}</p>}
                  {canManage && (
                    <div className="commissioning-actions">
                      <button type="button" disabled={loading} onClick={() => saveCommissioning(selectedCommissioning.stage)}><ShieldCheck size={13} /> GUARDAR EVIDENCIA</button>
                      {selectedCommissioning.stage === "planned" && <button type="button" disabled={loading} onClick={() => saveCommissioning("bench")}>INICIAR BANCO <ChevronRight size={13} /></button>}
                      {selectedCommissioning.stage === "bench" && <button type="button" disabled={loading} onClick={() => saveCommissioning("field")}>PASAR A CAMPO <ChevronRight size={13} /></button>}
                      {selectedCommissioning.stage === "field" && <button type="button" disabled={loading} onClick={() => saveCommissioning("certified")}>CERTIFICAR <BadgeCheck size={13} /></button>}
                      {selectedCommissioning.stage === "failed" && <button type="button" disabled={loading} onClick={() => saveCommissioning("bench")}>REABRIR EN BANCO <ChevronRight size={13} /></button>}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {view === "catalog" && (
          <div className="profile-catalog">
            <section className="profile-list">
              {profiles.map((profile) => (
                <button type="button" className={selectedProfile?.id === profile.id ? "is-selected" : ""} onClick={() => setSelectedProfileId(profile.id)} key={profile.id}>
                  <span><FileSearch size={17} /></span>
                  <span><small>{profile.code} · {profile.category}</small><strong>{profile.manufacturer} {profile.model}</strong><em>{profile.protocols_json.join(" · ")}</em></span>
                  <b>{profile.lifecycle_status}</b>
                </button>
              ))}
            </section>
            <section className="profile-detail">
              {selectedProfile && (
                <>
                  <div className="profile-hero"><BookOpenCheck size={34} /><span><small>{selectedProfile.code}</small><h3>{selectedProfile.manufacturer} {selectedProfile.model}</h3><b>{selectedProfile.lifecycle_status.toUpperCase()}</b></span></div>
                  <div className="profile-specs">
                    <article><small>ALIMENTACIÓN</small><strong>{selectedProfile.power_spec}</strong></article>
                    <article><small>AMBIENTE</small><strong>{selectedProfile.environmental_spec}</strong></article>
                    <article><small>CONEXIÓN</small><strong>{selectedProfile.connection_types_json.join(" · ")}</strong></article>
                    <article><small>PROTOCOLOS</small><strong>{selectedProfile.protocols_json.join(" · ")}</strong></article>
                  </div>
                  <div className="profile-columns">
                    <article><span className="eyebrow">QUÉ VAMOS A MEDIR</span>{selectedProfile.measurements_json.map((item) => <p key={item}><CheckCircle2 size={12} /> {item}</p>)}</article>
                    <article><span className="eyebrow">DATOS PARA CONFIGURAR</span>{selectedProfile.required_config_json.map((item) => <p key={item}><CircleDashed size={12} /> {item}</p>)}</article>
                  </div>
                  <div className="profile-sources"><span className="eyebrow">FUENTES Y FICHAS</span>{selectedProfile.source_urls_json.length ? selectedProfile.source_urls_json.map((url) => <a href={url} target="_blank" rel="noreferrer" key={url}><ExternalLink size={12} /> {new URL(url).hostname}</a>) : <p>PROVEEDOR POR DEFINIR · SOLICITAR FICHA, PROTOCOLO Y MUESTRA</p>}</div>
                </>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
